import React, { useState } from 'react';
import { Star, MapPin, Phone, Mail, Award, Languages } from 'lucide-react';
import Button from '../../../components/ui/Button';

import SelectUI from '../../../components/ui/Select';


const ConsultantMatcher = ({ service, consultants, loading, onConsultantSelect, onBack }) => {
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedSpecialization, setSelectedSpecialization] = useState('');

  const mockPropertyConsultants = [
    {
      id: 'c088fe7c-4ac9-4876-95c3-b604a2d19d5b',
      name: 'Marie Kouassi',
      specialization: 'Investissement Immobilier',
      languages: ['Français', 'Anglais'],
      experience: '8 ans d\'expérience',
      rating: 4.8,
      reviews: 127,
      location: 'Cocody, Abidjan',
      phone: '+225 01 41 07 02 71',
      email: 'contact@kapitalstates.com',
      avatar: ''
    },
    {
      id: '8e01c2f4-a0a2-4d8d-8ab0-ec9ed3da0b0b',
      name: 'Jean-Baptiste Traoré',
      specialization: 'Analyse de Marché',
      languages: ['Français'],
      experience: '12 ans d\'expérience',
      rating: 4.9,
      reviews: 89,
      location: 'Plateau, Abidjan',
      phone: '+225 01 41 07 02 71',
      email: 'contact@kapitalstates.com',
      avatar: ''
    }
  ];

  const activeConsultants = service?.id === 'legal_consultation'
    ? consultants
    : mockPropertyConsultants;

  const availableLanguages = [...new Set(
    (Array.isArray(activeConsultants) ? activeConsultants : [])?.flatMap(c =>
      c?.languages || ['Français']
    )
  )];

  const availableSpecializations = [...new Set(
    (Array.isArray(activeConsultants) ? activeConsultants : [])?.map(c =>
      c?.specialization || c?.specializations?.[0]
    )?.filter(Boolean)
  )];

  const filteredConsultants = (Array.isArray(activeConsultants) ? activeConsultants : [])?.filter(c => {
    const langs = c?.languages || ['Français'];
    const spec = c?.specialization || c?.specializations?.[0];
    return (!selectedLanguage || langs?.includes(selectedLanguage))
      && (!selectedSpecialization || spec === selectedSpecialization);
  });

  const getName = (c) => c?.name || c?.notary_name || c?.user_profile?.full_name || 'Nom non disponible';
  const getEmail = (c) => c?.email || c?.user_profile?.email || '';
  const getPhone = (c) => c?.phone || c?.user_profile?.phone || '';
  const getLocation = (c) => c?.location || c?.office_address || 'Localisation non disponible';

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Chargement des consultants…</p>
      </div>
    );
  }

  const langOptions = [
    { value: '', label: 'Toutes les langues' },
    ...availableLanguages?.map(l => ({ value: l, label: l }))
  ];

  const specOptions = [
    { value: '', label: 'Toutes les spécialisations' },
    ...availableSpecializations?.map(s => ({ value: s, label: s }))
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="outline" size="sm" iconName="ArrowLeft" iconPosition="left" onClick={onBack}>
          Retour
        </Button>
        <div>
          <h2 className="text-2xl font-heading font-semibold text-foreground">
            Choisissez votre consultant
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {service?.name} — Trouvez l'expert qui vous convient
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Filtres
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectUI
            label="Langue préférée"
            options={langOptions}
            value={selectedLanguage}
            onChange={setSelectedLanguage}
            placeholder="Toutes les langues"
          />
          <SelectUI
            label="Spécialisation"
            options={specOptions}
            value={selectedSpecialization}
            onChange={setSelectedSpecialization}
            placeholder="Toutes les spécialisations"
          />
        </div>
      </div>

      {/* Consultant cards */}
      <div className="space-y-4">
        {filteredConsultants?.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-10 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Aucun consultant ne correspond à vos critères.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedLanguage(''); setSelectedSpecialization(''); }}
            >
              Réinitialiser les filtres
            </Button>
          </div>
        ) : (
          filteredConsultants?.map((consultant) => (
            <div
              key={consultant?.id}
              className="bg-card border border-border rounded-lg p-5 hover:border-primary/40 hover:shadow-sm transition-all duration-200"
            >
              <div className="flex flex-col lg:flex-row gap-5">
                {/* Avatar + name */}
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="w-14 h-14 rounded-full bg-muted overflow-hidden flex-shrink-0 flex items-center justify-center">
                    {consultant?.avatar || consultant?.user_profile?.avatar_url ? (
                      <img
                        src={consultant?.avatar || consultant?.user_profile?.avatar_url}
                        alt={getName(consultant)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-lg font-semibold text-muted-foreground">
                        {getName(consultant)?.[0]?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-sm">{getName(consultant)}</p>
                    <p className="text-xs text-primary font-medium mt-0.5">
                      {consultant?.specialization || consultant?.specializations?.[0] || 'Consultant'}
                    </p>
                    {consultant?.experience && (
                      <p className="text-xs text-muted-foreground mt-0.5">{consultant?.experience}</p>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    {consultant?.rating && (
                      <div className="flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                        <span className="text-sm font-medium text-foreground">{consultant?.rating}</span>
                        <span className="text-xs text-muted-foreground">({consultant?.reviews} avis)</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      {getLocation(consultant)}
                    </div>
                    {consultant?.languages && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Languages className="w-3.5 h-3.5 flex-shrink-0" />
                        {consultant?.languages?.join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {getPhone(consultant) && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                        {getPhone(consultant)}
                      </div>
                    )}
                    {getEmail(consultant) && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                        {getEmail(consultant)}
                      </div>
                    )}
                    {consultant?.license_number && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Award className="w-3.5 h-3.5 flex-shrink-0" />
                        Licence: {consultant?.license_number}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action */}
                <div className="flex items-center lg:flex-shrink-0">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => onConsultantSelect?.(consultant)}
                    className="w-full lg:w-auto"
                  >
                    Choisir
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConsultantMatcher;